import { useState } from "react";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/virtualizedTable";
import { 
  useGetTemplate, 
  TemplateTask, 
  TaskTemplate,
  DueDateOffset 
} from "@/lib/api/checklist/templates";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Clock, 
  Users,
  ArrowUpDown,
  ChevronLeft,
  Play 
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useListMinimalUsers } from "@/lib/api/users";

interface TemplateDetailViewProps {
  templateId: string;
  onBack: () => void;
  onEditTemplate: (template: TaskTemplate) => void;
  onAddTask: () => void;
  onEditTask: (task: TemplateTask) => void;
  onDeleteTask: (task: TemplateTask) => void;
  onReorderTasks?: () => void;
  onTriggerTemplate: (template: TaskTemplate) => void;
}

function formatDueDateOffset(offset: DueDateOffset): string {
  const parts = [];
  if (offset.days) parts.push(`${offset.days} day${offset.days !== 1 ? 's' : ''}`);
  if (offset.hours) parts.push(`${offset.hours} hour${offset.hours !== 1 ? 's' : ''}`);
  if (offset.minutes) parts.push(`${offset.minutes} minute${offset.minutes !== 1 ? 's' : ''}`);
  
  if (parts.length === 0) return "Immediately";
  return parts.join(", ");
}

export function TemplateDetailView({
  templateId,
  onBack,
  onEditTemplate,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onTriggerTemplate,
  onReorderTasks,
}: TemplateDetailViewProps) {
  const templateGetResult = useGetTemplate(templateId);
  const usersMinimalListRes = useListMinimalUsers();
  const users = usersMinimalListRes?.data?.data || [];

  const { isLoading } = templateGetResult;
  const template = templateGetResult?.data?.data;

  const columnHelper = createColumnHelper<TemplateTask>();

  const getUserNames = (userIds?: string[]): string => {
    if (!userIds || userIds.length === 0) return "Unassigned";
    
    const names = userIds.map(id => {
      const user = users.find((u: { id: string }) => u.id === id);
      return user?.name || "Unknown";
    });
    
    if (names.length === 1) return names[0];
    if (names.length === 2) return names.join(" & ");
    return `${names[0]} & ${names.length - 1} others`;
  };

  const columns = [
    columnHelper.accessor("title", {
      id: "name",
      header: "Task Name",
      meta: { isCellClick: true },
      cell: (info) => {
        const task = info.row.original;
        return (
          <div>
            <div className="font-medium">
              {task.title}
            </div>
            {task.description && (
              <p className="text-sm text-muted-foreground truncate max-w-md">
                {task.description}
              </p>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("due_date_offset", {
      id: "due_date",
      header: "Due Date",
      enableColumnFilter: false,
      cell: (info) => {
        const offset = info.getValue();
        return (
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              {formatDueDateOffset(offset)}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor("auto_assigned_user_ids", {
      id: "assigned_to",
      header: "Auto-Assigned To",
      enableColumnFilter: false,
      cell: (info) => {
        const userIds = info.getValue();
        const names = getUserNames(userIds);
        
        return (
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              {names}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: "Created",
      filterFn: "dateTimeFilter" as any,
      cell: (info) => {
        const date = new Date(info.getValue() as any);
        return (
          <span className="text-sm text-muted-foreground">
            {date.toLocaleDateString()}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const task = row.original;
        return (
          <div className="flex gap-2 flex-row items-center">
            <Button
              size="icon"
              variant="outline"
              onClick={() => onEditTask(task)}
              className="h-8 w-8"
              title="Edit Task"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              onClick={() => onDeleteTask(task)}
              className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive hover:border-destructive"
              title="Delete Task"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    }),
  ];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Template not found</p>
        <Button variant="link" onClick={onBack}>
          Go back
        </Button>
      </div>
    );
  }

  const tasks = template.tasks || [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="h-8 w-8"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-2xl font-semibold">{template.name}</h2>
            <Badge variant={template.active ? "default" : "secondary"}>
              {template.active ? "Active" : "Inactive"}
            </Badge>
          </div>
          {template.description && (
            <p className="text-muted-foreground ml-10">{template.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => onTriggerTemplate(template)}
          >
            <Play className="mr-2 h-4 w-4" />
            Activate Template
          </Button>
          <Button
            variant="outline"
            onClick={() => onEditTemplate(template)}
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit Template
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            Template Tasks ({tasks.length})
          </h3>
          <div className="flex gap-2">
            {onReorderTasks && tasks.length > 1 && (
              <Button
                variant="outline"
                onClick={onReorderTasks}
              >
                <ArrowUpDown className="mr-2 h-4 w-4" />
                Reorder Tasks
              </Button>
            )}
            <Button onClick={onAddTask}>
              <Plus className="mr-2 h-4 w-4" />
              Add Task
            </Button>
          </div>
        </div>

        {tasks.length === 0 ? (
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            <p className="text-muted-foreground mb-4">
              No tasks added to this template yet
            </p>
            <Button onClick={onAddTask}>
              <Plus className="mr-2 h-4 w-4" />
              Add First Task
            </Button>
          </div>
        ) : (
          <DataTable
            onCellClick={(row) => onEditTask(row as TemplateTask)}
            data={tasks}
            columns={columns as any[]}
            height="500px"
            defaultSorting={[{ id: "created_at", desc: false }]}
          />
        )}
      </div>
    </div>
  );
}
